import { CollectionState } from './CollectionState';
import {
  CollectionEvents,
  ComputedQuery,
  ComputedResult,
  FiltersMap,
  KeyedItem,
} from '../model';
import {
  action,
  makeObservable,
  observable,
  runInAction,
} from 'mobx';
import { TaskState } from './TaskState';
import { ReportBI } from '../services';
import {
  cairoCtaClicked,
  cairoItemSelectionToggled,
  cairoSearchResults,
  componentDismissed,
  loadEnd,
  loadMore,
  newItemCreationEnd,
  pickerModalUsability,
  pickerPickModalCtaButtonClickOrImmediateCta,
} from '@wix/bex-utils/@wix/bi-logger-os-data/v2';
import { withoutIrrelevant } from '../util';
import { Snapshot } from './snapshot';
import { MultiCollectionSupport } from './MultiCollectionSupport';
import { onItemsCreated } from './onItemsCreated';

const DEFAULT_FLOATING_NOTIFICATION_HEIGHT = 54;

export type OnPickerReady<F extends FiltersMap> = (params: {
  query: ComputedQuery<F>;
  result: ComputedResult;
}) => unknown;

export interface PickerEvents<T, F extends FiltersMap>
  extends CollectionEvents<F> {
  readonly onReady?: OnPickerReady<F>;
  readonly onInitialFetchError?: (params: {
    err: unknown;
    isOnline: boolean;
  }) => unknown;
  readonly onItemSelectClick?: (params: {
    item: T;
    index: number;
    previousIndex: number | null;
  }) => unknown;
}

export interface PickerStateParams<T, F extends FiltersMap> {
  getMaxItems: () => number | undefined;
  collection: CollectionState<T, F>;
  minContentHeight?: string | number;
  events?: PickerEvents<T, F>;
  shouldFilterNotAffectTotalCount?: (filterName: keyof F) => boolean;
  loadStartTime?: number;
  reportBi: ReportBI;
}

const withoutDefaults = withoutIrrelevant<
  'currentTab' | 'currentFilters' | 'currentSortOrder' | 'currentView'
>();

export class PickerState<T, F extends FiltersMap = FiltersMap> {
  readonly collection: PickerStateParams<T, F>['collection'];
  readonly multi;
  readonly shouldFilterNotAffectTotalCount: (filterName: keyof F) => boolean;
  readonly events: PickerEvents<T, F>;

  readonly initTask = new TaskState();
  readonly reportBi;
  readonly loadStartTime;

  isScrollTop: boolean;

  minContentHeight: string | number;
  floatingNotificationHeight = 0;

  readonly getMaxItems;

  readonly filteredListSize;

  constructor(params: Readonly<PickerStateParams<T, F>>) {
    this.getMaxItems = params.getMaxItems;
    this.collection = params.collection;
    this.reportBi = params.reportBi;

    this.multi = new MultiCollectionSupport({
      collections: [this.collection],
    });

    this.shouldFilterNotAffectTotalCount =
      params.shouldFilterNotAffectTotalCount ?? (() => false);
    this.isScrollTop = true;
    this.minContentHeight = params.minContentHeight ?? 300;

    this.events = params.events ?? {};

    this.loadStartTime = params.loadStartTime ?? performance.now();

    this.filteredListSize = new Snapshot({
      expression: () =>
        Object.values(this.collection.query.customFilters).map((f) => f?.value),
      effect: () => this.collection.result.total,
    });

    makeObservable(this, {
      setFloatingNotificationHeight: action,
      toggle: action,
      updateIsScrollTop: action,
      onPrimaryButtonClick: action,
      isScrollTop: observable.ref,
    });
  }

  async clearResultAndMoveToStart({ force }: { force?: boolean } = {}) {
    await Promise.all([
      this.collection.clearResultAndMoveToStart({ force }),
    ]);
  }

  updateIsScrollTop(newValue: boolean) {
    this.isScrollTop = newValue;
  }

  _commonDynamicBiParams(): {
    filteredListSize?: number;
    listSize?: number;
    maxItems?: number;
  } {
    const { collection, filteredListSize } = this;

    return {
      ...collection._commonDynamicBiParams(),
      listSize: collection.result.total,
      filteredListSize: filteredListSize.value,
      maxItems: this.getMaxItems(),
    };
  }

  onNewPage = () => {
    const { reportBi } = this;

    const startTime = performance.now();

    return ({ totalNewItems }: { totalNewItems: number }) => {
      reportBi(
        withoutDefaults(loadMore)({
          ...this._commonDynamicBiParams(),
          loadingTime: Math.round(performance.now() - startTime),
          loadedItems: totalNewItems,
        }),
      );
    };
  };

  onSearchStart = () => {
    const { collection, reportBi } = this;

    const { result } = collection;

    const startTime = performance.now();
    reportBi(
      pickerModalUsability({
        ...this._commonDynamicBiParams(),
        action: 'search',
        searchTerm: collection.query.search.value,
      }),
    );

    return () => {
      reportBi(
        cairoSearchResults({
          ...this._commonDynamicBiParams(),
          loadingTime: Math.round(performance.now() - startTime),
          searchResultsCnt: result.total,
          searchTerm: collection.query.search.value,
        }),
      );
    };
  };

  init() {
    const {
      collection,
      initTask,
      events: { onReady, onInitialFetchError },
      reportBi,
      filteredListSize,
    } = this;

    collection.emitter.on('newPageStart', this.onNewPage);
    collection.emitter.on('searchStart', this.onSearchStart);

    const disposers = [
      collection.init(),
      filteredListSize.init(),
    ];

    initTask.runOnce(async () => {
      const { result, query } = collection;

      try {
        await Promise.all([
          collection.initTask.status.promise,
        ]);

        filteredListSize.forceUpdate();

        runInAction(() => {
          try {
            reportBi(
              withoutDefaults(loadEnd)({
                ...this._commonDynamicBiParams(),
                url: typeof window !== 'undefined' ? window.location.href : '',
                route:
                  typeof window !== 'undefined' ? window.location.pathname : '',
                loadingTime: Math.round(performance.now() - this.loadStartTime),
                initialItems: result.size,
              }),
            );
            onReady?.({
              query: query.asComputed,
              result: result.asComputed,
            });
          } catch (e) {
            collection.errorMonitor.captureException(e);
          }
        });
      } catch (err) {
        await onInitialFetchError?.({
          err,
          isOnline: collection.onlineState.isOnline,
        });
        throw err;
      }
    });

    return () => {
      collection.emitter.off('newPageStart', this.onNewPage);
      collection.emitter.off('searchStart', this.onSearchStart);
      for (const disposer of disposers) {
        disposer();
      }
    };
  }

  onCloseButtonClick() {
    const { reportBi } = this;

    reportBi(
      withoutDefaults(componentDismissed)({
        origin: 'X button',
      }),
    );
  }

  onBackButtonClick() {
    const { _onButtonClick } = this;

    _onButtonClick({ ctaName: 'back', numLevels: 2, currentLevel: 2 });
  }

  onContinueButtonClick() {
    const { _onButtonClick } = this;

    _onButtonClick({ ctaName: 'continue-next', numLevels: 2, currentLevel: 1 });
  }

  onSecondaryButtonOnClick() {
    const { reportBi } = this;

    reportBi(
      withoutDefaults(componentDismissed)({
        origin: 'Cancel button',
      }),
    );
  }

  onPrimaryButtonClick({
    currentLevel,
    numLevels,
  }: {
    currentLevel: number;
    numLevels: number;
  }) {
    const {
      collection: { bulkSelect },
      _onButtonClick,
      reportBi,
    } = this;

    const { selectedCount } = bulkSelect;

    _onButtonClick({ ctaName: 'continue', currentLevel, numLevels });

    reportBi(
      pickerPickModalCtaButtonClickOrImmediateCta({
        ...this._commonDynamicBiParams(),
        isImmediate: false,
        itemsCnt: selectedCount,
      }),
    );
  }

  _onButtonClick = ({
    ctaName,
    currentLevel,
    numLevels,
  }: {
    ctaName: string;
    currentLevel: number;
    numLevels: number;
  }) => {
    this.reportBi(
      cairoCtaClicked({
        ...this._commonDynamicBiParams(),
        ctaIndex: 1,
        numCtas: 1,
        currentLevel,
        numLevels,
        location: 'picker',
        moreActionsIndex: 0,
        ctaName,
        url: typeof window !== 'undefined' ? window.location.href : '',
      }),
    );
  };

  toggle(
    keyedItem: KeyedItem<T>,
    options: { clear?: boolean; hasAdditionalStep?: boolean } = {},
  ) {
    const {
      collection,
      events: { onItemSelectClick },
      reportBi,
    } = this;
    const {
      bulkSelect,
      query: { search },
    } = collection;
    const { select, selectedCountOrTotal: numItemsBefore } = bulkSelect;

    const {
      selectedValues: [prevSelected],
    } = select;

    if (options.clear) {
      bulkSelect.select.clear();
    }

    bulkSelect.toggle(keyedItem);
    reportBi(
      cairoItemSelectionToggled({
        ...this._commonDynamicBiParams(),
        url: typeof window !== 'undefined' ? window.location.href : '',
        isSelectAll: false,
        isFromSearch: !search.isEmpty,
        searchQuery: search.value,
        itemIndex: keyedItem.index,
        numLevels: options.hasAdditionalStep ? 2 : 1,
        itemId: keyedItem.key,
        clickType: bulkSelect.isChecked(keyedItem.key)
          ? 'Selection'
          : 'Deselection',
        numItemsBefore,
        numItemsAfter: bulkSelect.selectedCountOrTotal,
      }),
    );
    onItemSelectClick?.({
      item: keyedItem.item,
      index: keyedItem.index,
      previousIndex:
        prevSelected != null
          ? (bulkSelect.getCollectionItem(prevSelected.key)?.index as
              | number
              | null)
          : null,
    });
  }

  setFloatingNotificationHeight(
    floatingNotificationHeight: number = DEFAULT_FLOATING_NOTIFICATION_HEIGHT,
  ) {
    this.floatingNotificationHeight = floatingNotificationHeight;
  }

  onCreateNew = action(async (items?: T[]) => {
    if (items) {
      await onItemsCreated(this.collection, {
        items,
        reportBi: (reportProps) => {
          this.reportBi({
            ...reportProps,
            params: {
              ...this._commonDynamicBiParams(),
              ...reportProps.params,
            },
          });
        },
      });
    } else {
      runInAction(() => {
        this.reportBi(
          newItemCreationEnd({
            ...this._commonDynamicBiParams(),
            numSelectedItems: this.collection.bulkSelect.select.selectedCount,
            resultType: 'dismissed',
          }),
        );
      });
    }
  });
}
